import { useRef, useState } from 'react'
import styles from './MultiSelect.module.css'
import { useClickOutside } from '../lib/useClickOutside'
import type { MultiSelectProps } from '../types'

export function MultiSelect({
  values,
  onChange,
  options,
  placeholder = 'All',
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, open, () => setOpen(false))

  const toggle = (value: string) => {
    if (values.includes(value)) onChange(values.filter((v) => v !== value))
    else onChange([...values, value])
  }

  const display =
    values.length === 0
      ? placeholder
      : values
          .map((v) => options.find((o) => o.value === v)?.label ?? v)
          .join(', ')

  return (
    <div ref={ref} className={`${styles.root} ${className ?? ''}`}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
        }}
      >
        <span
          className={values.length === 0 ? styles.placeholder : styles.value}
        >
          {display}
        </span>
        {values.length > 0 && (
          <span
            className={styles.clear}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              onChange([])
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                onChange([])
              }
            }}
            aria-label="Clear selection"
          >
            ×
          </span>
        )}
        <span className={styles.chevron}>▾</span>
      </button>
      {open && (
        <ul className={styles.popover} role="listbox">
          {options.length === 0 ? (
            <li className={styles.empty}>No options</li>
          ) : (
            options.map((opt) => {
              const checked = values.includes(opt.value)
              return (
                <li key={opt.value}>
                  <label
                    className={`${styles.option} ${checked ? styles.optionChecked : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              )
            })
          )}
        </ul>
      )}
    </div>
  )
}
